import React from 'react';

import type { Nullable } from '@clearverify/types';

import { EmployerDocument, useEmployerLogoQuery, useEmployerUpdateMutation } from 'src/api';
import { useNotification } from 'src/hooks/app';
import { DroppedFile } from 'src/types/file';

import { useFileUpload } from '../file';

export const useEmployerLogo = (employerId: Nullable<string>) => {
  const { createApolloContext } = useNotification();

  const { uploadFile, uploading } = useFileUpload();

  const [employerUpdateMutation, { loading: employerLogoUpdating }] = useEmployerUpdateMutation({
    awaitRefetchQueries: true,
    refetchQueries: [EmployerDocument],
    context: createApolloContext({
      errorMessage: 'Something went wrong while updating logo',
    }),
  });

  const { data, loading: employerLogoLoading } = useEmployerLogoQuery({
    variables: {
      id: employerId || '',
    },
    skip: !employerId,
  });

  const employer = data?.employer;
  const employerLogo = employer?.logo;

  const updateEmployerLogo = React.useCallback(
    async (logoFile: DroppedFile | string) => {
      // TODO: handle case with fileId
      if (typeof logoFile === 'string') throw new Error('Not implemented');
      if (!logoFile.file) return;

      try {
        const uploadedFile = await uploadFile(logoFile.file);
        await employerUpdateMutation({
          variables: {
            where: { id: employerId },
            data: {
              logo: {
                connect: { id: uploadedFile.id },
              },
            },
          },
        });
      } catch (error) {
        console.error(error);
      }
    },
    [employerId, employerUpdateMutation, uploadFile],
  );

  return {
    employerLogo,
    employerLogoLoading,
    updateEmployerLogo,
    employerLogoUpdating: uploading || employerLogoUpdating,
  };
};
